import { all, put, call, takeLatest } from 'redux-saga/effects'
import queryString from 'query-string'

import axios from 'portal/utils/axios-msal'

import {
  DESIGN_MATCHES_LOAD,
  designMatchesLoadSuccess,
  designMatchesLoadFailure
} from './actions'
import {
  DesignMatchesApiProduct,
  DesignMatchesApiRequirement,
  DesignMatchesApiResponse,
  DesignMatchCondition,
  DesignMatchRow,
  DesignMatchRowState
} from './types'

const toValue = (value: unknown) => `${value ?? ''}`

const isMatchStatus = (status: unknown) =>
  toValue(status).trim().toLowerCase() === 'match'

function normaliseRowState(rawState: unknown): DesignMatchRowState {
  return toValue(rawState).trim().toLowerCase() === 'closed' ? 'closed' : 'open'
}

// TODO: remove the 'suppliers' array fallback once the API always returns 'supplier'
function normaliseSupplier(product: Partial<DesignMatchesApiProduct>): string {
  if (typeof product?.supplier === 'string') {
    return product.supplier
  }
  return Array.isArray(product?.suppliers) ? product.suppliers.join(', ') : ''
}

function normaliseRequirement(
  productId: string,
  requirement: Partial<DesignMatchesApiRequirement>,
  index: number
): DesignMatchCondition {
  return {
    id: `${productId}-requirement-${index}`,
    condition: toValue(requirement?.condition),
    required: toValue(requirement?.required),
    delivered: toValue(requirement?.delivered),
    records: Number(requirement?.records || 0),
    status: toValue(requirement?.status),
    note: toValue(requirement?.note)
  }
}

function normaliseProduct(
  product: Partial<DesignMatchesApiProduct>,
  id: string
): DesignMatchRow {
  const requirements = Array.isArray(product?.requirements)
    ? product.requirements
    : []
  const conditions = requirements.map((requirement, index) =>
    normaliseRequirement(id, requirement, index)
  )
  const matchedCount = conditions.filter(condition =>
    isMatchStatus(condition.status)
  ).length
  const requirementsCount = product?.requirementsCount ?? conditions.length

  return {
    id,
    product: toValue(product?.name),
    supplier: normaliseSupplier(product),
    productGroup: toValue(product?.productGroup),
    lastUpdatedAt: toValue(product?.lastUpdatedAt),
    records: Number(product?.records || 0),
    requirements: `${matchedCount} of ${requirementsCount} met`,
    state: normaliseRowState(product?.state),
    conditions
  }
}

export function* designMatchesLoadSaga(action) {
  const {
    accountId,
    projectId,
    page = 1,
    loadedItems,
    productGroupNames = [],
    supplierNames = []
  } = action

  try {
    const requestParams: Record<string, unknown> = {
      pageNumber: page,
      productGroups: productGroupNames
    }
    // Omit the supplier filter entirely when nothing is selected, rather than sending an empty list
    if (supplierNames.length > 0) {
      requestParams.suppliers = supplierNames
    }

    const response: { data: DesignMatchesApiResponse } = yield call(() =>
      axios.get(
        `/accounts/${accountId}/projects/${projectId}/modules/delivery/records/designmatchesv2/product-grouped`,
        {
          params: requestParams,
          paramsSerializer: params => queryString.stringify(params)
        }
      )
    )

    const products = Array.isArray(response?.data?.products)
      ? response.data.products
      : []
    const items = products.map((product, index) =>
      normaliseProduct(product, `design-match-${loadedItems.length + index}`)
    )
    const totalCount =
      response?.data?.total ?? loadedItems.length + items.length
    const hasNextPage = loadedItems.length + items.length < totalCount

    yield put(
      designMatchesLoadSuccess(
        [...loadedItems, ...items],
        totalCount,
        hasNextPage
      )
    )
  } catch (error) {
    yield put(designMatchesLoadFailure(error))
  }
}

export function* watch() {
  yield all([takeLatest(DESIGN_MATCHES_LOAD, designMatchesLoadSaga)])
}
